import type { FC } from 'react'
import { IconButton, TablePaginationProps, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@dhl/icons'
import { tss } from '@dhl/theme'

const useStyles = tss.makeStyles()((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}))

type Props = Pick<TablePaginationProps, 'count' | 'page' | 'rowsPerPage' | 'onPageChange'> & {
  onRowsPerPageChange?: (rowsPerPage: number) => void
  rowsPerPageOptions?: number[]
  hideTotal?: boolean
}

export const Pagination: FC<Props> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange: doRowsPerPageChange,
  rowsPerPageOptions = [10, 20, 50],
  hideTotal = false,
}) => {
  const { classes } = useStyles()
  const { t } = useTranslation()

  const getLabelDisplayedRowsTo = () => {
    if (count === -1) {
      return (page + 1) * rowsPerPage
    }
    return rowsPerPage === -1 ? count : Math.min(count, (page + 1) * rowsPerPage)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onRowsPerPageChange = (e: any, value: number) => {
    if (value) {
      doRowsPerPageChange!(value)
      onPageChange(e, 0)
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.pagination}>
        <IconButton size="small" onClick={(e) => onPageChange(e, 0)} disabled={page === 0}>
          <FirstPage fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <div className={classes.label}>
          {t(hideTotal ? 'components.pagination.label_no_total' : 'components.pagination.label', {
            from: count === 0 ? 0 : page * rowsPerPage + 1,
            to: getLabelDisplayedRowsTo(),
            count: count === -1 ? -1 : count,
          })}
        </div>
        <IconButton
          size="small"
          onClick={(e) => onPageChange(e, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          <LastPage fontSize="small" />
        </IconButton>
      </div>

      {onRowsPerPageChange && rowsPerPageOptions.length > 0 && (
        <ToggleButtonGroup size="small" value={rowsPerPage} onChange={onRowsPerPageChange} exclusive>
          {rowsPerPageOptions.map((rowsPerPageOption) => (
            <ToggleButton
              key={rowsPerPageOption}
              value={rowsPerPageOption}
              disabled={rowsPerPageOption === rowsPerPage}
            >
              {rowsPerPageOption}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </div>
  )
}
